import { useMediaQuery } from '@mui/material'
import { useEffect, useState } from 'react'
import theme from 'utils/theme'

// The launcher is an "Ask Goldie" pill: a fixed height, and as wide as its label.
export const LAUNCHER_HEIGHT = 48
export const LAUNCHER_RIGHT = 24
// Bottom-right at every width, the convention for a chat launcher. `narrow` used
// to sit at 96px to clear a full-width primary button, but that is what put the
// launcher in the MIDDLE of a long page on a phone — Greg, 22 Sep, over the
// checkout's "Credit or debit card" row. pinnedBottomBarHeight() below already
// lifts it above anything genuinely pinned to the viewport bottom, so the blanket
// offset only ever pushed it over ordinary in-flow content.
export const LAUNCHER_BOTTOM = { narrow: 24, wide: 24 }
const GAP = 16
// Room left at the end of a page so its last control (a checkout's "Complete
// Trade", say) can scroll clear of the launcher instead of ending up under it.
export const LAUNCHER_CLEARANCE =
  Math.max(LAUNCHER_BOTTOM.narrow, LAUNCHER_BOTTOM.wide) + LAUNCHER_HEIGHT + GAP

const PINNED = new Set(['fixed', 'sticky'])

// Columns to probe: the pill's left edge, middle and right edge, so a bar that
// only reaches part of the way under it still counts. Before the launcher has a
// size (first paint, or jsdom), just the column its right end will occupy.
function probeColumns(launcher: HTMLElement | null): number[] {
  const rect = launcher?.getBoundingClientRect()
  if (rect && rect.width > 0) {
    return [rect.left + 1, rect.left + rect.width / 2, rect.right - 1]
  }
  return [window.innerWidth - LAUNCHER_RIGHT - LAUNCHER_HEIGHT / 2]
}

// Height of a bar pinned to the bottom of the viewport under the launcher (a
// sticky "Review and pay" footer, say), or 0. Probes the launcher's columns with
// elementsFromPoint and walks up to a fixed/sticky (or <footer>) ancestor that
// touches the viewport bottom; tall pinned things (side columns, overlays) are
// ignored.
export function pinnedBottomBarHeight(launcher: HTMLElement | null): number {
  if (typeof document.elementsFromPoint !== 'function') return 0
  const viewportHeight = window.innerHeight
  let height = 0

  probeColumns(launcher).forEach(x => {
    document.elementsFromPoint(x, viewportHeight - 2).forEach(hit => {
      if (launcher?.contains(hit)) return
      for (
        let el: Element | null = hit;
        el && el !== document.body;
        el = el.parentElement
      ) {
        // BaseLayout's stickyFooter is an absolutely positioned <footer>.
        const pinned =
          PINNED.has(window.getComputedStyle(el).position) ||
          el.tagName === 'FOOTER'
        if (!pinned) continue
        const rect = el.getBoundingClientRect()
        const touchesBottom = rect.bottom >= viewportHeight - 2
        if (touchesBottom && rect.height <= viewportHeight * 0.4) {
          height = Math.max(height, viewportHeight - rect.top)
        }
        break
      }
    })
  })
  return height
}

// Bottom offset (px) that keeps the launcher clear of pinned bottom CTAs.
export function useLauncherBottom(
  launcherRef: React.RefObject<HTMLElement>
): number {
  const narrow = useMediaQuery(theme.breakpoints.down('sm'))
  const base = narrow ? LAUNCHER_BOTTOM.narrow : LAUNCHER_BOTTOM.wide
  const [barHeight, setBarHeight] = useState(0)

  useEffect(() => {
    let frame = 0
    const measure = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() =>
        setBarHeight(pinnedBottomBarHeight(launcherRef.current))
      )
    }
    measure()
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, { passive: true })
    // Pages mount their sticky footers after data loads.
    const observer = new MutationObserver(measure)
    observer.observe(document.body, { childList: true, subtree: true })
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure)
      observer.disconnect()
    }
  }, [launcherRef])

  return barHeight > 0 ? Math.max(base, barHeight + GAP) : base
}
